/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Button, Grid, Typography, Typography2, breakpoints } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';

import { TrackedLink2 } from 'bundles/page/components/TrackedLink2';
import { useTracker, useVisibilityTracker } from 'bundles/page/lib/event-pulse/react';
import { useEventingData } from 'bundles/unified-career-academy/contexts/EventingContext';

import PROFILE_IMG from 'images/unified-career-academy/Profile.svg';

import _t from 'i18n!nls/career-academy';

const styles = {
  container: () => css`
    padding: var(--cds-spacing-600) var(--cds-spacing-1400);
    min-height: 350px;
    max-width: 1600px;
    margin: 50px auto;
    ${breakpoints.down('sm')} {
      padding: var(--cds-spacing-300);
    }
  `,
  title: () => css`
    padding-bottom: var(--cds-spacing-150);
  `,
  subtitle: () => css`
    padding-bottom: var(--cds-spacing-300);
  `,
  img: () => css`
    flex-direction: column;
    display: flex;
    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-300);
    }
  `,
};

const RolePageShowcaseModule: React.FC = () => {
  const eventingData = useEventingData();
  const track = useTracker();

  const viewProfileEventingV3Data = {
    ...eventingData,
    button: {
      name: 'view_profile' as ButtonName,
    },
  };

  const showcaseProfileRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_button',
    viewProfileEventingV3Data
  );

  return (
    <Grid container css={styles.container} alignContent="space-between">
      <Grid item xs={12} sm={6} ref={showcaseProfileRef}>
        <Typography variant="h1semibold" component="h2" css={styles.title}>
          {_t('Your dream job is out there.')}
          <br />
          {_t('Are you ready?')}
        </Typography>
        <Typography2 component="p" variant="bodyPrimary" color="supportText" css={styles.subtitle}>
          {_t(
            'Your Coursera Skills Profile will help you prepare for the next step in your career by showing employers your certificates, new skills and job-relevant projects.'
          )}
        </Typography2>
        <Button
          variant="secondary"
          icon={<ExternalLinkIcon size="small" />}
          iconPosition="after"
          size="small"
          aria-label={_t('View your profile. Opens in new tab')}
          component={TrackedLink2}
          href="/account-profile"
          trackingName="role_page_showcase_cta"
          target="_blank"
          onClick={() => track('click_button', viewProfileEventingV3Data)}
        >
          {_t('View your profile')}
        </Button>
      </Grid>
      <Grid item xs={12} sm={6} css={styles.img}>
        <Imgix
          alt={_t('Graphic of the learner profile page')}
          src={PROFILE_IMG}
          role="presentation"
          maxWidth="100%"
          maxHeight="100%"
        />
      </Grid>
    </Grid>
  );
};

export default RolePageShowcaseModule;
